"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { api } from "@/lib/api";
import { Badge, Button, Card, EmptyState, Input, Modal, Select, Skeleton } from "@/components/ui";
import { useToast } from "@/components/toast";
import { useConfirm } from "@/components/confirm";
import { EMPTY_FILTER, describeSegment, filterToBody, filterToParams, segmentToFilter } from "@/lib/contact-segments";
import type { Contact, ContactGroup, ContactSegment, SegmentFilter } from "@/lib/types";

export function Segments({ groups, onChanged }: { groups: ContactGroup[]; onChanged?: () => void }) {
  const toast = useToast();
  const confirm = useConfirm();

  const [segments, setSegments] = useState<ContactSegment[] | null>(null);
  const [filter, setFilter] = useState<SegmentFilter>(EMPTY_FILTER);
  const [preview, setPreview] = useState<{ total: number; sample: Contact[] } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [editing, setEditing] = useState<ContactSegment | null>(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api<{ segments: ContactSegment[] }>("account/segments")
      .then((r) => setSegments(r.segments || []))
      .catch(() => setSegments([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Live preview, debounced — the filter changes on every keystroke, and each evaluation joins the
  // address book to the message history, so we don't want one per character.
  const qs = useMemo(() => filterToParams(filter).toString(), [filter]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    setPreviewing(true);
    timer.current = setTimeout(() => {
      const p = new URLSearchParams(qs);
      p.set("limit", "5"); // just enough to show a sample; `total` is the real answer
      api<{ contacts: Contact[]; total: number }>(`account/contacts/query?${p.toString()}`)
        .then((r) => setPreview({ total: r.total, sample: r.contacts || [] }))
        .catch(() => setPreview(null))
        .finally(() => setPreviewing(false));
    }, 350);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [qs]);

  async function save() {
    if (!name.trim()) { toast.error("Give the segment a name."); return; }
    setSaving(true);
    try {
      await api("account/segments", {
        method: "POST",
        body: { segmentId: editing?.segmentId ?? null, name: name.trim(), ...filterToBody(filter) }
      });
      toast.success(editing ? "Segment updated." : "Segment saved.");
      setSaveOpen(false);
      setEditing(null);
      setName("");
      load();
      onChanged?.();
    } catch (e) { toast.error((e as Error).message); }
    finally { setSaving(false); }
  }

  async function remove(s: ContactSegment) {
    const ok = await confirm({
      title: "Delete segment",
      message: <>Delete <strong>{s.name}</strong>? The contacts it matches are <strong>kept</strong> — only the saved filter is removed.</>,
      confirmLabel: "Delete",
      danger: true
    });
    if (!ok) return;
    try {
      await api("account/segments", { method: "DELETE", body: { segmentId: s.segmentId } });
      toast.success(`Deleted "${s.name}".`);
      load();
      onChanged?.();
    } catch (e) { toast.error((e as Error).message); }
  }

  const needsDays = filter.messaged === "not-in" || filter.messaged === "within";

  return (
    <Card
      title="Segments"
      actions={
        <Button
          size="sm"
          onClick={() => { setEditing(null); setName(""); setSaveOpen(true); }}
          disabled={(preview?.total ?? 0) === 0}
        >
          Save as segment
        </Button>
      }
    >
      <p className="mb-3 text-sm text-gray-600">
        A group is a list you build by hand. A <strong>segment</strong> is a question that re-answers itself —
        “everyone in VIP I haven&apos;t messaged in 30 days” shrinks as you send to them.
      </p>

      {/* Builder */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Select label="Group" value={filter.groupId} onChange={(e) => setFilter((f) => ({ ...f, groupId: e.target.value }))}>
          <option value="">All contacts</option>
          {groups.map((g) => <option key={g.groupId} value={g.groupId}>{g.name}</option>)}
        </Select>

        <Select
          label="Messaging history"
          value={filter.messaged}
          onChange={(e) => setFilter((f) => ({ ...f, messaged: e.target.value as SegmentFilter["messaged"] }))}
        >
          <option value="any">Any</option>
          <option value="not-in">Not messaged in…</option>
          <option value="within">Messaged within…</option>
          <option value="never">Never messaged</option>
        </Select>

        {needsDays ? (
          <Input
            label="Days"
            type="number"
            min={1}
            value={filter.days}
            onChange={(e) => setFilter((f) => ({ ...f, days: e.target.value.replace(/\D/g, "").slice(0, 4) }))}
          />
        ) : (
          <div className="hidden sm:block" />
        )}

        <div className="grid grid-cols-2 gap-2">
          <Input
            label="Variable"
            value={filter.attrKey}
            onChange={(e) => setFilter((f) => ({ ...f, attrKey: e.target.value }))}
            placeholder="city"
          />
          <Input
            label="equals"
            value={filter.attrValue}
            onChange={(e) => setFilter((f) => ({ ...f, attrValue: e.target.value }))}
            placeholder="Mogadishu"
          />
        </div>
      </div>

      {/* Live result */}
      <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-gray-50/60 px-4 py-3 dark:bg-gray-500/10">
        {previewing ? (
          <Skeleton className="h-6 w-40" />
        ) : (
          <>
            <span className="text-lg font-bold tabular-nums text-gray-900">
              {(preview?.total ?? 0).toLocaleString()}
            </span>
            <span className="text-sm text-gray-600">contact{preview?.total === 1 ? "" : "s"} match right now</span>
            {(preview?.sample.length ?? 0) > 0 && (
              <span className="text-xs text-gray-400">
                e.g. {preview!.sample.slice(0, 3).map((c) => c.name || c.msisdn).join(", ")}
              </span>
            )}
          </>
        )}
        {filter.messaged === "not-in" && (
          <span className="w-full text-xs text-gray-500">
            Includes contacts you&apos;ve never messaged — they haven&apos;t been messaged in {filter.days || "N"} days either.
          </span>
        )}
      </div>

      {/* Saved segments */}
      <div className="mt-5">
        {segments === null ? (
          <div className="space-y-2">{Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-12" />)}</div>
        ) : segments.length === 0 ? (
          <EmptyState
            title="No saved segments"
            description="Build a filter above, then save it. Segments can be sent to like a group — but they re-evaluate every time."
          />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {segments.map((s) => (
              <div key={s.segmentId} className="rounded-xl border border-gray-200 p-4">
                <p className="font-semibold text-gray-900">{s.name}</p>
                <p className="mt-0.5 text-xs text-gray-500">{describeSegment(s)}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link
                    href={`/sms/bulk?segmentId=${s.segmentId}`}
                    className="rounded-lg bg-brand-600 px-2.5 py-1.5 text-xs font-medium text-pure-white transition hover:bg-brand-700"
                  >
                    Send to segment
                  </Link>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => { setFilter(segmentToFilter(s)); setEditing(s); setName(s.name); }}
                  >
                    Load
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(s)}>Delete</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <p className="mt-3 text-xs text-gray-500">
          Editing <strong>{editing.name}</strong> — adjust the filter above, then{" "}
          <button className="font-medium text-brand-600 hover:underline" onClick={() => { setName(editing.name); setSaveOpen(true); }}>
            save changes
          </button>{" "}
          or{" "}
          <button className="font-medium text-gray-600 hover:underline" onClick={() => { setEditing(null); setFilter(EMPTY_FILTER); }}>
            cancel
          </button>.
        </p>
      )}

      <Modal
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        title={editing ? "Save changes" : "Save as segment"}
        footer={
          <>
            <Button variant="secondary" onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input
            label="Segment name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. VIP — gone quiet"
            autoFocus
          />
          <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3 text-sm dark:bg-gray-500/10">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">This segment asks</p>
            <p className="mt-1 text-gray-800">
              {describeSegment({
                segmentId: 0,
                name: "",
                groupId: filter.groupId ? Number(filter.groupId) : null,
                groupName: groups.find((g) => String(g.groupId) === filter.groupId)?.name ?? null,
                neverMessaged: filter.messaged === "never",
                notMessagedDays: filter.messaged === "not-in" ? Number(filter.days) : null,
                messagedWithinDays: filter.messaged === "within" ? Number(filter.days) : null,
                attrKey: filter.attrKey.trim() || null,
                attrValue: filter.attrValue.trim() || null,
                createdDate: ""
              })}
            </p>
            <p className="mt-2 text-xs text-gray-500">
              Matches <strong>{(preview?.total ?? 0).toLocaleString()}</strong> contact(s) today —
              re-checked every time you send.
            </p>
          </div>
        </div>
      </Modal>
    </Card>
  );
}
